import { memo, useCallback, useRef, useState } from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { Colors, Text } from "@zapier/design-system";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import {
  groupNodeCreateStartAtom,
  isDraggingNodeAtom,
  nodeClassNameAtomFamily,
  nodesAtom,
  saveNodeOnResizeEndAtom,
  shouldShowStepIdsAtom,
  singleSelectedNodeIdAtom,
  GroupNodeData,
} from "@zapier/canvas-state";

import { GroupIcon } from "../../../icons/GroupIcon";
import useUpdateNode from "../../../../hooks/useUpdateNode";
import { StepId } from "../../../../components/StepId";

import { getRelativeNodesBounds } from "./getRelativeNodesBounds";
import { FormInput } from "./FormInput";
import { nodeResizerHandleStyle, nodeResizerLineStyle } from "../constants";
import { GroupNodeFloatingToolbar } from "./GroupNodeFloatingToolbar";
import { lightenColor } from "../../../../utils/lightenColor";
import styles from "./GroupNode.module.css";

const childNodesRectAtomFamily = atomFamily((id: string) => {
  const childNodesRectAtom = atom((get) => {
    const nodes = get(nodesAtom);
    const childNodes = nodes.filter((n) => n.parentId === id);
    if (childNodes.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    return getRelativeNodesBounds(childNodes);
  });

  childNodesRectAtom.debugLabel = `childNodesRectAtomFamily(${id})`;
  return childNodesRectAtom;
});

function GroupNode({ id, selected, data }: NodeProps<Node<GroupNodeData>>) {
  const rect = useAtomValue(childNodesRectAtomFamily(id));
  const [isEditing, setIsEditing] = useState(false);
  const color = data.color ?? Colors.GrayWarm5;
  const [label, setLabel] = useState(data.label);
  const updateNodeAndSave = useUpdateNode();
  const ref = useRef<HTMLInputElement>(null);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const className = useAtomValue(nodeClassNameAtomFamily(id));
  const shouldHighlight = className?.includes("highlight");
  const isDraggingNode = useAtomValue(isDraggingNodeAtom);
  const groupNodeCreateStart = useAtomValue(groupNodeCreateStartAtom);
  const singleSelectedNodeId = useAtomValue(singleSelectedNodeIdAtom);
  const isSelected =
    !groupNodeCreateStart && !isDraggingNode && singleSelectedNodeId === id;
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndAtom);

  const minWidth = rect.x + rect.width;
  const minHeight = rect.y + rect.height;

  const borderColor = data.color === Colors.PrimeWhite ? "#D7D3C9" : color;

  function getBackgroundColor() {
    if (color === Colors.PrimeWhite) {
      return Colors.PrimeWhite;
    }

    // This is the default
    if (color === Colors.GrayWarm5) {
      return "#E8E7E480";
    }

    return lightenColor(color);
  }

  const origBackgroundColor = getBackgroundColor();
  const backgroundColor = shouldHighlight
    ? lightenColor(origBackgroundColor, 0.5, 1)
    : origBackgroundColor;

  const handleEditLabel = useCallback(
    (value: string) => {
      setIsEditing(false);
      updateNodeAndSave({
        data: {
          label: value,
        },
      });
    },
    [setIsEditing, updateNodeAndSave],
  );

  const handleOnDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      // Stop event propagation to prevent reactflow from putting group node in selected state
      event.stopPropagation();
      setIsEditing(true);
      if (ref.current) {
        setTimeout(() => {
          ref.current?.focus();
        }, 0);
      }
    },
    [setIsEditing],
  );

  const handleOnClick = useCallback((event: React.MouseEvent) => {
    // We stop propagation to prevent reactflow from putting group node in selected state
    event.stopPropagation();
  }, []);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  return (
    <>
      {isSelected && <GroupNodeFloatingToolbar id={id} color={color} />}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions*/}
      <div
        className={styles.label}
        onDoubleClick={handleOnDoubleClick}
        onClick={handleOnClick}
      >
        <GroupIcon fill={Colors.GrayWarm10} height="14px" width="14px" />
        {!isEditing && (
          <Text type="SmallPrint3">{data.label || "Untitled group"}</Text>
        )}
        {isEditing && (
          <FormInput
            type="text"
            label="Asset Name"
            ref={ref}
            maxLength={50}
            value={label as string}
            onBlur={(e) =>
              handleEditLabel((e.target as HTMLInputElement).value)
            }
            onChange={(value) => setLabel(value)}
            onSubmit={() => handleEditLabel(label)}
          />
        )}
      </div>
      <NodeResizer
        handleStyle={nodeResizerHandleStyle}
        lineStyle={nodeResizerLineStyle}
        isVisible={selected}
        minHeight={minHeight}
        minWidth={minWidth}
        onResizeEnd={handleResizeEnd}
      />
      <div
        className={styles.wrapper}
        style={{
          border: shouldHighlight
            ? "2px solid #3d4592"
            : `1px solid ${borderColor}`,
          backgroundColor,
        }}
      />
      {shouldShowStepIds && <StepId />}
    </>
  );
}

export default memo(GroupNode);
